/**
 * One run at a time, with no lost work.
 *
 * Flushing queued alert decisions is triggered from two places — acting on an
 * alert, and reconciling when the tab becomes visible. Run concurrently, both
 * passes read the same pending intent and POST the same decision twice. This
 * wrapper lets only one pass be in flight; a caller that arrives during one
 * joins it, and marks the running pass to sweep the queue again before it
 * settles, so work enqueued after the current pass read the queue is still
 * sent rather than dropped.
 */
export function singleFlight(task: () => Promise<void>): () => Promise<void> {
  let inFlight: Promise<void> | undefined;
  let again = false;

  return function run(): Promise<void> {
    if (inFlight) {
      again = true;
      return inFlight;
    }
    const started = (async () => {
      try {
        do {
          again = false;
          await task();
        } while (again);
      } finally {
        inFlight = undefined;
        again = false;
      }
    })();
    inFlight = started;
    return started;
  };
}
